import { useEffect, useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '@renderer/store/useSessionStore';

/**
 * Sprint 71 — "¿Sabías que…?": el recordatorio de dónde está la guía.
 *
 * Pedido del negocio: *"sólo quiero que PRIMEROS PASOS tenga un color distinguible o parpadee o
 * pongas un modal que diga ¿SABÍAS QUE..? … esto para que sepa a dónde ir mientras se acostumbra"*.
 *
 * Tres decisiones que conviene no revertir:
 *
 *  1. **No parpadea.** Un elemento que destella en una pantalla de trabajo se vuelve ruido en dos
 *     días —el ojo aprende a ignorarlo— y además es un problema de accesibilidad. El color
 *     permanente en la barra hace el mismo trabajo sin gritar. Este modal es el que enseña; el
 *     color es el que recuerda.
 *  2. **Se apaga solo cuando deja de ser cierto.** Cuando ya hay de todo capturado, el aviso no
 *     aparece aunque la casilla siga marcada: seguir diciéndole "empieza por aquí" a quien ya
 *     terminó es cómo se le enseña al operador a cerrar los avisos sin leerlos.
 *  3. **Lo decide ella, no yo.** La casilla viene marcada —porque lo que pidió es justamente verlo
 *     mientras se acostumbra— y se guarda en cuanto la desmarca. Nada de "no volver a mostrar" a
 *     los tres cierres, que es adivinarle.
 *
 * Sólo para el dueño: es quien captura el catálogo. A cocina y caja este aviso les estorbaría.
 */

/** Clave en `ConfiguracionSistema`. La familia `tip_` está en la whitelist del handler. */
const CLAVE = 'tip_primeros_pasos_mostrar';

interface Props {
  /**
   * Cuántos pasos faltan. Si es 0, el aviso no se muestra: la guía dejó de ser noticia.
   * `null` mientras se averigua.
   */
  pasosPendientes: number | null;
}

export function TipPrimerosPasos({ pasosPendientes }: Props) {
  const sesion = useSessionStore((s) => s.sesion);
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [mostrarSiempre, setMostrarSiempre] = useState(true);

  const esDueno = sesion?.rol === 'dueño';

  useEffect(() => {
    if (!esDueno || pasosPendientes === null || pasosPendientes === 0) return;
    let vivo = true;
    void window.api.config.obtener(CLAVE).then((res) => {
      if (!vivo) return;
      // Sin valor guardado se muestra: es el estado de un negocio que acaba de instalar.
      const mostrar = !res.ok || res.data !== 'false';
      setMostrarSiempre(mostrar);
      setAbierto(mostrar);
    });
    return () => {
      vivo = false;
    };
  }, [esDueno, pasosPendientes]);

  if (!abierto) return null;

  const cerrar = async () => {
    setAbierto(false);
    // Se guarda sólo si cambió: no vale la pena escribir en la base por cerrar un aviso.
    if (!mostrarSiempre) {
      await window.api.config.actualizar(CLAVE, 'false');
    }
  };

  const irALaGuia = async () => {
    await cerrar();
    navigate('/primeros-pasos');
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-4">
          <Lightbulb className="h-6 w-6 text-amber-500" />
          <h2 className="text-lg font-bold">¿Sabías que…?</h2>
        </div>

        <div className="space-y-3 px-5 py-4 text-sm text-slate-700">
          <p>
            En <strong>Primeros pasos</strong> tienes la lista de todo lo que hay que capturar para
            poder vender, <strong>en el orden en que conviene hacerlo</strong>. Cada paso se pone en
            verde solo cuando lo terminas.
          </p>
          {/*
            Sprint 82 — El orden cambió. Empezaba por Inventario porque un producto tenía que
            engancharse a un ingrediente ya existente; con el alta nueva, dar de alta el producto
            **crea** su inventario, y seguir el orden viejo produce un choque en el primer
            producto ("ya tienes eso en tu inventario"). Un instructivo que enseña a chocar es
            peor que no tener instructivo.
          */}
          <ul className="space-y-1.5 rounded-lg bg-slate-50 p-3">
            <li>
              <strong>1. Categorías</strong> — los grupos de botones del punto de venta{' '}
              <em>(opcional, pero ahorra trabajo)</em>.
            </li>
            <li>
              <strong>2. Productos</strong> — lo que vendes, con su precio, sus tamaños y cuánto
              tienes. <strong>El inventario se arma solo desde aquí.</strong>
            </li>
            <li>
              <strong>3. Paquetes</strong> — combinaciones con precio propio <em>(opcional)</em>.
            </li>
            <li>
              <strong>4. Promociones</strong> — 2x1, regalos, precios por horas{' '}
              <em>(opcional)</em>.
            </li>
            <li>
              <strong>5. Mesas</strong> — para atender en el local.
            </li>
            <li>
              <strong>6. Impresora</strong> — para que los tickets salgan solos{' '}
              <em>(opcional)</em>.
            </li>
          </ul>
          <p className="rounded-lg bg-blue-50 px-3 py-2 text-blue-900">
            <strong>Las existencias no son un paso previo.</strong> Se llena solo con lo que das de alta;
            entras ahí después, para agregar lo que llega y ver lo que queda.
          </p>
          <p>
            Lo encuentras siempre en la barra de la izquierda, abajo, marcado en{' '}
            <span className="rounded bg-amber-100 px-1 font-medium text-amber-900">ámbar</span>.
          </p>
          {pasosPendientes !== null && pasosPendientes > 0 && (
            <p className="font-medium text-slate-800">
              Ahora mismo te {pasosPendientes === 1 ? 'falta 1 paso' : `faltan ${pasosPendientes} pasos`}.
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={mostrarSiempre}
              onChange={(e) => setMostrarSiempre(e.target.checked)}
            />
            <span>Mostrar siempre</span>
          </label>
          <div className="flex gap-2">
            <button onClick={() => void irALaGuia()} className="btn-primary min-h-[44px]">
              Ir a Primeros pasos
            </button>
            <button onClick={() => void cerrar()} className="btn-ghost min-h-[44px]">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
